import React, { useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../config';
import 'superdoc/style.css';
import './SuperDocEditor.css';

/**
 * SuperDocEditor - React wrapper for SuperDoc DOCX editor
 * Replaces the old TipTap-based DocumentEditor with native DOCX editing
 * 
 * @param {string} documentId - ID of the document to edit (optional)
 * @param {File|string} documentFile - DOCX file object or URL to load (optional)
 * @param {Function} onSave - Callback when document is saved (receives blob/file)
 * @param {Function} onCancel - Callback when editor is cancelled
 * @param {Object} config - Additional SuperDoc configuration options
 */
const SuperDocEditor = ({ 
  documentId, 
  documentFile, 
  onSave, 
  onCancel,
  config = {} 
}) => {
  const containerRef = useRef(null);
  const toolbarRef = useRef(null);
  const editorRef = useRef(null);
  const objectUrlRef = useRef(null); // Track object URLs we create
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    let SuperDoc = null;
    let editorInstance = null;

    const initializeEditor = async () => {
      // Don't re-initialize if editor already exists
      if (editorRef.current) {
        console.log('Editor already initialized, skipping');
        return;
      }

      try {
        // Dynamic import to avoid SSR issues
        const superdocModule = await import('superdoc');
        SuperDoc = superdocModule.SuperDoc || superdocModule.default?.SuperDoc || superdocModule.default;

        if (!SuperDoc) {
          throw new Error('SuperDoc not found. Make sure superdoc package is installed.');
        }

        if (!containerRef.current || !toolbarRef.current || !mounted) {
          console.log('Container or toolbar not ready or unmounted');
          // Retry after a short delay
          const retryTimer = setTimeout(() => {
            if (mounted) {
              initializeEditor();
            }
          }, 100);
          return () => clearTimeout(retryTimer);
        }

        // Get container references
        const container = containerRef.current;
        const toolbar = toolbarRef.current;
        
        // Clear container safely - use innerHTML to avoid React node conflicts
        if (container) {
          // Use innerHTML to completely clear - avoids React's node tracking
          container.innerHTML = '';
        }
        
        // Clear toolbar container
        if (toolbar) {
          toolbar.innerHTML = '';
        }

        // Determine document source
        let documentSource = null;
        if (documentFile) {
          if (documentFile instanceof File) {
            // Revoke previous URL if exists
            if (objectUrlRef.current) {
              URL.revokeObjectURL(objectUrlRef.current);
            }
            // Store in ref so cleanup can access it
            objectUrlRef.current = URL.createObjectURL(documentFile);
            documentSource = objectUrlRef.current;
          } else {
            documentSource = documentFile;
          }
        } else if (documentId) {
          // Load document from backend
          documentSource = `${API_BASE_URL}/api/documents/${documentId}/download`;
        }

        if (!documentSource) {
          throw new Error('No document source provided (need documentFile or documentId)');
        }

        console.log('Initializing SuperDoc with document:', documentSource);

        // SuperDoc expects string selectors (CSS selectors), not DOM elements
        // Create unique IDs for container and toolbar
        const containerId = `superdoc-container-${documentId || 'new'}-${Date.now()}`;
        const toolbarId = `superdoc-toolbar-${documentId || 'new'}-${Date.now()}`;
        
        container.id = containerId;
        
        // Set toolbar ID (already checked above)
        toolbar.id = toolbarId;
        
        const containerSelector = `#${containerId}`;
        const toolbarSelector = `#${toolbarId}`;
        
        console.log('Initializing SuperDoc with container:', containerSelector, 'toolbar:', toolbarSelector);

        // Initialize SuperDoc editor with explicit toolbar configuration
        // Note: SuperDoc should preserve all formatting (including highlighting) automatically
        // when exporting DOCX, as it's a native DOCX editor
        const superDocConfig = {
          selector: containerSelector, // Editor container selector (must be string)
          document: documentSource,
          documentMode: 'editing',
          modules: {
            toolbar: {
              selector: toolbarSelector // Toolbar container selector (must be string)
            }
          },
          onReady: () => {
            console.log('SuperDoc ready');
            if (mounted) {
              editorRef.current = editorInstance;
              setLoading(false);
            }
          },
          onError: (err) => {
            console.error('SuperDoc error:', err);
            if (mounted) {
              setError(err.message || 'Failed to load document');
              setLoading(false);
              editorInstance = null;
            }
          }
        };
        
        // Merge user config if provided (avoid overriding our critical config)
        if (config && typeof config === 'object') {
          Object.keys(config).forEach(key => {
            // Don't override selector, document, documentMode, or modules
            if (key !== 'selector' && key !== 'document' && key !== 'documentMode' && key !== 'modules') {
              superDocConfig[key] = config[key];
            }
          });
        }
        
        console.log('Creating SuperDoc instance with config:', superDocConfig);
        editorInstance = new SuperDoc(superDocConfig);

      } catch (err) {
        console.error('Error initializing SuperDoc:', err);
        if (mounted) {
          setError(err.message || 'Failed to initialize editor');
          setLoading(false);
        }
        editorInstance = null;
      }
    };

    // Initialize editor - wait for container to be ready
    let initTimer = null;
    let loadingTimeout = null;
    
    // Set a timeout to show error if loading takes too long (30 seconds)
    loadingTimeout = setTimeout(() => {
      if (mounted && loading) {
        console.warn('SuperDoc loading timeout - editor took too long to initialize');
        setError('Document editor is taking too long to load. Please refresh the page or try again later.');
        setLoading(false);
      }
    }, 30000);
    
    // Use a small delay to ensure the container is rendered
    // Initialize when container is available (regardless of loading state initially)
    initTimer = setTimeout(() => {
      if (mounted && containerRef.current) {
        // Container is ready, initialize SuperDoc
        console.log('Container ready, initializing SuperDoc', {
          container: containerRef.current,
          documentId,
          documentFile,
          documentSource: documentFile || (documentId ? `${API_BASE_URL}/api/documents/${documentId}/download` : 'none')
        });
        initializeEditor();
      } else {
        console.log('Container not ready yet, will retry...', {
          containerRef: containerRef.current,
          mounted
        });
        // Retry after longer delay
        const retryTimer = setTimeout(() => {
          if (mounted && containerRef.current) {
            console.log('Retry: Container ready, initializing SuperDoc');
            initializeEditor();
          }
        }, 1000);
        return () => clearTimeout(retryTimer);
      }
    }, 200);

    return () => {
      // Clear timers if they exist
      if (initTimer) {
        clearTimeout(initTimer);
      }
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }
      mounted = false;
      
      // Cleanup: destroy editor instance FIRST, then clear container
      // This prevents SuperDoc from trying to unmount nodes React has removed
      if (editorRef.current) {
        try {
          // Try to destroy using the destroy method
          if (typeof editorRef.current.destroy === 'function') {
            console.log('Destroying SuperDoc editor');
            editorRef.current.destroy();
          }
        } catch (e) {
          console.warn('Error destroying SuperDoc editor:', e);
        }
        
        editorRef.current = null;
      }

      // Don't clear container here - let SuperDoc finish its cleanup first
      // We'll clear it in a delayed timeout at the end of cleanup function

      // Cleanup object URL if we created one (do this last)
      if (objectUrlRef.current) {
        try {
          URL.revokeObjectURL(objectUrlRef.current);
        } catch (e) {
          console.warn('Error revoking object URL:', e);
        }
        objectUrlRef.current = null;
      }
      
      // Clear container after SuperDoc has finished cleanup
      // Use a delay to ensure SuperDoc's internal cleanup is complete
      const clearTimer = setTimeout(() => {
        if (containerRef.current) {
          try {
            containerRef.current.innerHTML = '';
          } catch (e) {
            console.warn('Error clearing container in delayed cleanup:', e);
          }
        }
      }, 200);
      
      // Return function to clear the timer if component unmounts before delay completes
      return () => clearTimeout(clearTimer);
    };
    // Only re-run when documentId or documentFile changes, not loading
    // This prevents unnecessary re-initializations when loading state changes
  }, [documentId, documentFile]);

  // Reusable function to export document as DOCX blob
  const exportDocumentAsDOCX = async () => {
    if (!editorRef.current) {
      throw new Error('Editor not initialized');
    }

    const editor = editorRef.current;
    
    // Give SuperDoc time to sync all formatting changes
    console.log('Waiting for formatting changes to sync...');
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Try to trigger a blur event on the editor to ensure changes are committed
    if (containerRef.current) {
      const editorElement = containerRef.current.querySelector('[contenteditable="true"]') || containerRef.current;
      if (editorElement) {
        editorElement.blur();
        editorElement.focus();
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    let documentBlob = null;
    
    // Method 1: Try export() method first
    if (typeof editor.export === 'function') {
      console.log('Trying SuperDoc export() method...');
      try {
        const exportPromise = editor.export({
          exportType: ['docx'],
          isFinalDoc: true,
          commentsType: 'clean',
          triggerDownload: false
        });
        
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Export timeout after 10 seconds')), 10000)
        );
        
        const result = await Promise.race([exportPromise, timeoutPromise]);
        
        if (result instanceof Blob) {
          documentBlob = result;
        } else if (Array.isArray(result) && result.length > 0 && result[0] instanceof Blob) {
          documentBlob = result[0];
        }
      } catch (e) {
        console.warn('export() method failed, trying alternatives:', e);
      }
    }
    
    // Method 2: Try exportToDOCX
    if (!documentBlob && typeof editor.exportToDOCX === 'function') {
      console.log('Trying exportToDOCX() method...');
      try {
        const result = await editor.exportToDOCX();
        if (result instanceof Blob) {
          documentBlob = result;
        }
      } catch (e) {
        console.warn('exportToDOCX() failed:', e);
      }
    }
    
    // Method 3: Try exportEditorsToDOCX
    if (!documentBlob && typeof editor.exportEditorsToDOCX === 'function') {
      console.log('Trying exportEditorsToDOCX() method...');
      try {
        const exportPromise = editor.exportEditorsToDOCX({ 
          isFinalDoc: true,
          commentsType: 'clean'
        });
        
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Export timeout after 10 seconds')), 10000)
        );
        
        const blobs = await Promise.race([exportPromise, timeoutPromise]);
        
        if (blobs && Array.isArray(blobs) && blobs.length > 0 && blobs[0] instanceof Blob) {
          documentBlob = blobs[0];
        }
      } catch (e) {
        console.warn('exportEditorsToDOCX() failed:', e);
      }
    }
    
    if (!documentBlob) {
      throw new Error('Could not export document. No export method returned a valid blob.');
    }
    
    return documentBlob;
  };

  const handleDownloadDOCX = async () => {
    try {
      setSaving(true);
      const blob = await exportDocumentAsDOCX();
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `document-${Date.now()}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('✅ DOCX download initiated');
    } catch (err) {
      console.error('Error downloading DOCX:', err);
      alert(`Failed to download document: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setSaving(true);
      
      // First, try server-side conversion
      try {
        const docxBlob = await exportDocumentAsDOCX();
        const formData = new FormData();
        formData.append('file', docxBlob, 'document.docx');
        
        console.log('Converting DOCX to PDF on server...');
        const response = await fetch(`${API_BASE_URL}/api/documents/convert-docx-to-pdf`, {
          method: 'POST',
          body: formData,
        });
        
        if (response.ok) {
          const pdfBlob = await response.blob();
          const url = URL.createObjectURL(pdfBlob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `document-${Date.now()}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          console.log('✅ PDF downloaded successfully');
          return;
        }
      } catch (serverErr) {
        console.warn('Server-side PDF conversion failed, using browser print:', serverErr);
      }
      
      // Fallback: Use browser's print-to-PDF functionality
      // Get the editor's content element
      if (!containerRef.current) {
        throw new Error('Editor container not found');
      }
      
      const editorElement = containerRef.current.querySelector('.superdoc-editor') || 
                            containerRef.current.querySelector('[contenteditable="true"]') ||
                            containerRef.current;
      
      // Create a new window with the document content for printing
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        throw new Error('Could not open print window. Please allow popups for this site.');
      }
      
      // Clone the editor content
      const contentClone = editorElement.cloneNode(true);
      
      // Create HTML structure for printing
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Document</title>
          <style>
            @page {
              margin: 1in;
            }
            body {
              font-family: 'Times New Roman', Times, serif;
              font-size: 12pt;
              line-height: 1.6;
              color: #000;
              max-width: 8.5in;
              margin: 0 auto;
              padding: 0;
            }
            p {
              margin: 0.5em 0;
            }
            h1, h2, h3, h4, h5, h6 {
              margin: 1em 0 0.5em 0;
            }
            * {
              box-sizing: border-box;
            }
          </style>
        </head>
        <body>
          ${contentClone.innerHTML}
        </body>
        </html>
      `);
      printWindow.document.close();
      
      // Wait for content to load, then trigger print dialog
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          // Close window after printing (optional)
          // printWindow.close();
        }, 250);
      };
      
      console.log('✅ Print dialog opened - select "Save as PDF" as the destination');
    } catch (err) {
      console.error('Error exporting PDF:', err);
      
      // Final fallback: download DOCX
      try {
        const docxBlob = await exportDocumentAsDOCX();
        const url = URL.createObjectURL(docxBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `document-${Date.now()}.docx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        alert(`Could not open PDF print dialog. DOCX file has been downloaded instead.\n\nYou can convert it to PDF using:\n• Microsoft Word (File > Save As > PDF)\n• Google Docs (File > Download > PDF)\n• Online converters\n\nError: ${err.message}`);
      } catch (fallbackErr) {
        alert(`Failed to export: ${err.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!editorRef.current) {
      console.error('Editor not initialized');
      alert('Editor not initialized. Please wait for the editor to load.');
      return;
    }

    setSaving(true);
    try {
      console.log('Attempting to save document with full formatting...');
      const documentBlob = await exportDocumentAsDOCX();

      if (documentBlob) {
        console.log('✅ Document blob obtained successfully!', {
          size: documentBlob.size,
          type: documentBlob.type
        });
        
        // Verify blob has content (size > 0)
        if (documentBlob.size === 0) {
          throw new Error('Exported document blob is empty. This may indicate formatting was not preserved.');
        }
        
        // Log blob info for debugging
        console.log('Document blob details:', {
          size: documentBlob.size,
          type: documentBlob.type,
          'size > 0': documentBlob.size > 0
        });
        
        // Call onSave callback with the document blob
        if (onSave) {
          await onSave(documentBlob, documentId);
        } else {
          console.warn('No onSave callback provided');
          alert('Document exported but no save handler configured');
        }
      } else {
        throw new Error('Could not export document. No export method returned a valid blob.');
      }
    } catch (err) {
      console.error('Error saving document:', err);
      setError(err.message || 'Failed to save document');
      alert(`Failed to save document: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <div className="superdoc-error">
        <div className="error-message">
          <h3>Error Loading Editor</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Reload Page</button>
          {onCancel && <button onClick={onCancel}>Cancel</button>}
        </div>
      </div>
    );
  }

  return (
    <div className="superdoc-editor-wrapper">
      <div className="superdoc-toolbar">
        <div className="toolbar-left">
          <button 
            onClick={handleSave} 
            disabled={saving || loading}
            className="save-button"
          >
            {saving ? 'Saving...' : '💾 Save'}
          </button>
          <button 
            onClick={handleDownloadDOCX} 
            disabled={saving || loading}
            className="download-button"
            title="Download as DOCX"
          >
            📥 DOCX
          </button>
          <button 
            onClick={handleExportPDF} 
            disabled={saving || loading}
            className="export-button"
            title="Export as PDF"
          >
            📄 PDF
          </button>
          {onCancel && (
            <button 
              onClick={onCancel} 
              disabled={saving}
              className="cancel-button"
            >
              Cancel
            </button>
          )}
        </div>
        <div className="toolbar-right">
          {loading && <span className="loading-indicator">Loading editor...</span>}
        </div>
      </div>
      
      {/* SuperDoc's built-in toolbar container - must exist before initialization */}
      <div 
        ref={toolbarRef}
        className="superdoc-toolbar"
        style={{ 
          width: '100%',
          minHeight: '40px',
          borderBottom: '1px solid #ddd',
          background: '#f5f5f5'
        }}
      />
      
      <div className="superdoc-container-wrapper" style={{ minHeight: '600px', position: 'relative' }}>
        {loading && (
          <div className="superdoc-loading" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10, background: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div className="spinner"></div>
            <p>Loading document editor...</p>
            <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>This may take a moment for large documents</p>
          </div>
        )}
        <div 
          ref={containerRef} 
          className="superdoc-container"
          style={{ 
            minHeight: '600px',
            width: '100%',
            position: 'relative',
            zIndex: loading ? 0 : 1,
            display: loading ? 'none' : 'block'
          }}
          // Don't use key - it causes React to unmount/remount and confuses SuperDoc
        />
      </div>
    </div>
  );
};

export default SuperDocEditor;

